import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { listFees, sendOverdueSms, formatOverdueSmsMessage } from './api/feesApi'
import { appToast } from '../../../components/AppToast'
import AppPagination from '../../../components/AppPagination'
import AppFormFooterBar from '../../../components/AppFormFooterBar'

function getInitials(name) {
  return String(name || '?')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

export default function FeesSendSmsScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const singleStudentId = searchParams.get('studentId')

  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [customTemplate, setCustomTemplate] = useState('')

  useEffect(() => {
    loadOverdueMessages()
  }, [singleStudentId])

  const loadOverdueMessages = async () => {
    try {
      setIsLoading(true)
      const students = await listFees({
        typeFilter: 'OverDue',
        studentId: singleStudentId ? Number(singleStudentId) : undefined,
      })

      const overdueList = (students || []).filter(
        (s) => Number(s.overdue || 0) > 0 || s.status === 'OverDue'
      )

      const formatted = overdueList.map((s) => ({
        id: s.id,
        studentName: s.studentName,
        course: s.course,
        batch: s.batch,
        rollNo: s.rollNo,
        overdue: s.overdue,
        mobile: s.mobile || '',
        message: formatOverdueSmsMessage(s),
      }))

      setMessages(formatted)
      setCurrentPage(1)
    } catch (err) {
      console.error('Failed to load overdue fee records:', err)
      appToast.error('Failed to load overdue fee records')
      setMessages([])
    } finally {
      setIsLoading(false)
    }
  }

  const totalOverdue = useMemo(() => {
    return messages.reduce((sum, m) => sum + Number(m.overdue || 0), 0)
  }, [messages])

  const totalPages = Math.max(1, Math.ceil(messages.length / pageSize))
  const paginatedMessages = useMemo(() => {
    return messages.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  }, [messages, currentPage, pageSize])

  const handleSend = async () => {
    if (messages.length === 0) {
      appToast.warning('No overdue student messages to send.')
      return
    }

    try {
      setIsSending(true)
      const studentIds = messages.map((m) => m.id)
      const res = await sendOverdueSms({
        studentIds,
        customMessage: customTemplate.trim() || undefined,
      })

      if (res.sentCount > 0) {
        appToast.success(res.message || `Successfully sent ${res.sentCount} SMS message(s)!`)
      } else {
        appToast.warning('No SMS messages were dispatched.')
      }

      navigate('/admission/fees/list')
    } catch (err) {
      console.error('Failed to send SMS:', err)
      appToast.error(err?.response?.data?.detail || err?.message || 'Failed to send SMS messages')
    } finally {
      setIsSending(false)
    }
  }

  const handleCancel = () => {
    navigate('/admission/fees/list')
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Page Header & Navigation ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-1">
            <Link to="/admission/fees/list" className="hover:text-primary transition-colors">
              Fees Management
            </Link>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span className="font-bold text-on-surface">Send Overdue Reminder SMS</span>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Send Overdue Fee SMS Reminders
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Preview, customize, and dispatch automated SMS notifications to parents of students with overdue fee installments.
          </p>
        </div>

        <button
          type="button"
          onClick={handleCancel}
          className="self-start sm:self-auto flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to Fees List</span>
        </button>
      </section>

      {/* ── Summary Statistics Banner ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-xs">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">group</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              Overdue Students
            </p>
            <p className="mt-0.5 text-xl font-extrabold text-on-surface">
              {messages.length} {messages.length === 1 ? 'Student' : 'Students'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-xs">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">payments</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              Total Overdue Amount
            </p>
            <p className="mt-0.5 text-xl font-extrabold text-purple-700 tabular-nums">
              ₹{totalOverdue.toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-xs">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">sms</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              Delivery Channel
            </p>
            <p className="mt-0.5 text-xl font-extrabold text-blue-700">SMS Gateway</p>
          </div>
        </div>
      </div>

      {/* ── Custom Template Banner ── */}
      <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-5 shadow-xs">
        <div className="flex items-center justify-between gap-2 mb-2">
          <label className="text-xs font-bold text-on-surface flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">edit_note</span>
            <span>Custom Message Template Override (Optional)</span>
          </label>
          <span className="text-[11px] text-on-surface-variant">
            Variables: <code className="bg-surface-container px-1 py-0.5 rounded text-[10px]">#StudentName#</code>,{' '}
            <code className="bg-surface-container px-1 py-0.5 rounded text-[10px]">#Amount#</code>
          </span>
        </div>
        <textarea
          rows={2}
          value={customTemplate}
          onChange={(e) => setCustomTemplate(e.target.value)}
          placeholder="Leave blank to use default template: 'Dear Parent, Rs.X is overdue towards fees of your child Y. Kindly pay at the earliest...'"
          className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-hidden"
        />
      </div>

      {/* ── Message Recipient List Table ── */}
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="flex items-center justify-between border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">mark_chat_unread</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              Message Recipient Queue ({messages.length})
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-outline-variant/15 bg-surface-container-low/50 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="px-5 py-3">Recipient Student</th>
                <th className="px-5 py-3">Course / Batch</th>
                <th className="px-5 py-3 text-right">Overdue</th>
                <th className="px-5 py-3">Parent Mobile</th>
                <th className="px-5 py-3">Message Body Preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary/40 block mb-2">
                      progress_activity
                    </span>
                    Loading overdue records...
                  </td>
                </tr>
              ) : messages.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-sm text-on-surface-variant">
                    <span className="material-symbols-outlined text-3xl text-emerald-600 block mb-2">
                      task_alt
                    </span>
                    No students currently have overdue fees matching current criteria.
                  </td>
                </tr>
              ) : (
                paginatedMessages.map((msg) => (
                  <tr key={msg.id} className="hover:bg-surface-container-low/30 transition">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-black text-primary">
                          {getInitials(msg.studentName)}
                        </div>
                        <div>
                          <p className="font-bold text-on-surface">{msg.studentName}</p>
                          <p className="text-[10px] text-on-surface-variant font-mono">Roll #{msg.rollNo}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-on-surface">{msg.course || '—'}</p>
                      <p className="text-[11px] text-on-surface-variant">{msg.batch || '—'}</p>
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-amber-700 tabular-nums">
                      ₹{Number(msg.overdue || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-5 py-3 font-mono text-on-surface">{msg.mobile || '—'}</td>
                    <td className="px-5 py-3 text-[11px] text-on-surface-variant max-w-md leading-relaxed">
                      {customTemplate.trim()
                        ? customTemplate
                            .replace('#StudentName#', msg.studentName)
                            .replace('#Amount#', `₹${Number(msg.overdue || 0).toLocaleString('en-IN')}`)
                        : msg.message}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {messages.length > pageSize && (
          <div className="flex items-center justify-between border-t border-outline-variant/15 px-6 py-3 bg-surface-container-low/20">
            <span className="text-xs text-on-surface-variant font-medium">
              Page {currentPage} of {totalPages}
            </span>
            <AppPagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
              onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              canPrevious={currentPage > 1}
              canNext={currentPage < totalPages}
            />
          </div>
        )}
      </div>

      {/* ── Fixed Action Bar ── */}
      <AppFormFooterBar
        onCancel={handleCancel}
        onSave={handleSend}
        isSaving={isSending}
        saveText={`Send ${messages.length} SMS`}
        saveIcon="send"
        disabled={messages.length === 0 || isSending}
      />
    </div>
  )
}
